/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { MediaType } from "@phonos/types";
import {
  ACCEPTED_CONTENT_TYPES,
  ACCEPTED_VIDEO_CONTENT_TYPES,
  NATIVELY_SUPPORTED_AUDIO_CONTENT_TYPES,
} from "constants/AcceptedFiles";
import { MediaParserDimensions, parseMedia } from "@remotion/media-parser";
import { NewRelicDecoratedError } from "utilities/noticeError";
import { readFileAsArrayBuffer } from "utilities/FileUtilities";
import * as MusicMetadata from "music-metadata-browser";
import { createLogger } from "utilities/Logger";

const logger = createLogger("MediaUtilities");
export class MediaDecodeError extends NewRelicDecoratedError {
  mediaType: MediaType;

  constructor(fileType: string, mediaType: MediaType, codec: string = "") {
    super("MediaDecodeError", { fileType, mediaType, codec });
    this.name = "MediaDecodeError";
    this.mediaType = mediaType;
  }
}

export class MediaDecodeDurationError extends MediaDecodeError {
  constructor(fileType: string, mediaType: MediaType) {
    super(fileType, mediaType);
    this.name = "MediaDecodeDurationError";
  }
}

export class MediaDecodeFormatError extends MediaDecodeError {
  constructor(fileType: string, mediaType: MediaType) {
    super(fileType, mediaType);
    this.name = "MediaDecodeFormatError";
  }
}

export class MediaDecodeDimensionsError extends MediaDecodeError {
  constructor(fileType: string, mediaType: MediaType) {
    super(fileType, mediaType);
    this.name = "MediaDecodeDimensionsError";
  }
}

export class MediaContentTypeError extends NewRelicDecoratedError {
  constructor(fileType: string) {
    super(fileType);
    this.name = "MediaContentTypeError";
  }
}

export type MediaInfo = {
  // This can be null if the duration could not be calculated client-side
  durationMs: number | null;
  mediaType: MediaType;
  videoCodec: string | undefined;
  dimensions: MediaParserDimensions | undefined;
};

export function getMessageFromMediaDecodeError(e: MediaDecodeError): string {
  // TOOD: We only have these specific messages for video files
  // Don't forget to remind product for a specific message for audio files
  if (e instanceof MediaDecodeError && e.mediaType === MediaType.VIDEO) {
    if (e instanceof MediaDecodeDurationError) {
      return I18n.t("load_video_file_duration_error");
    } else if (e instanceof MediaDecodeDimensionsError) {
      return I18n.t("load_video_file_too_large_error");
    } else if (
      e instanceof MediaDecodeFormatError ||
      e instanceof MediaDecodeError
    ) {
      return I18n.t("load_video_file_error");
    }
  }

  if (e instanceof MediaDecodeError && e.mediaType === MediaType.AUDIO) {
    if (e instanceof MediaDecodeDurationError) {
      return I18n.t("load_audio_file_duration_error");
    }
    return I18n.t("load_audio_file_error");
  }

  // Catch-all for unknown errors
  return I18n.t("load_file_error");
}

export function validateMediaContentType(file: File): void {
  if (!ACCEPTED_CONTENT_TYPES.includes(file.type)) {
    throw new MediaContentTypeError(file.type);
  }
}

/**
 * Note - this is end-to-end tested implicitly by feature tests like the uploading_spec tests
 * It was exceedingly difficult to get MusicMetadata and parseMedia working on actual files in a jest environment.
 * So the unit tests mock them out.
 * @param file
 */
export async function getMediaInfo(file: File): Promise<MediaInfo> {
  let durationInSeconds: number | undefined;
  let videoCodec: string | undefined;
  let dimensions: MediaParserDimensions | undefined;

  // Default to audio, but if it's a video filetype, treat it as video.
  let mediaType: MediaType = MediaType.AUDIO;
  if (ACCEPTED_VIDEO_CONTENT_TYPES.includes(file.type)) {
    mediaType = MediaType.VIDEO;
  }

  // We can use @remotion/media-parser for duration, dimensions etc for video files.
  // Currently supports: .mp4, .mov, .webm, .mkv, .avi, .ts, .mp3, .wav, .aac and .flac
  if (!NATIVELY_SUPPORTED_AUDIO_CONTENT_TYPES.includes(file.type)) {
    try {
      const result = await parseMedia({
        acknowledgeRemotionLicense: true,
        src: file,
        fields: {
          durationInSeconds: true,
          videoCodec: true,
          dimensions: true,
        },
      });

      if (result.videoCodec) {
        videoCodec = result.videoCodec;
        dimensions = result.dimensions ?? undefined;
      } else {
        mediaType = MediaType.AUDIO;
      }

      if (typeof result.durationInSeconds === "number") {
        durationInSeconds = result.durationInSeconds;
      }
    } catch (mediaParserError) {
      logger.error(mediaParserError);
      newrelic.noticeError(mediaParserError);
    }
  } else {
    try {
      // Only use MusicMetadata for wav/mp3 files
      const fileBuffer = await readFileAsArrayBuffer(file);
      const metadata = await MusicMetadata.parseBuffer(
        new Uint8Array(fileBuffer),
        {},
        {
          duration: true,
        }
      );

      if (typeof metadata?.format?.duration === "number") {
        durationInSeconds = metadata.format.duration;
      }
    } catch (musicMetadataError) {
      logger.error(musicMetadataError);
      newrelic.noticeError(musicMetadataError);

      throw new MediaDecodeFormatError(file.type, mediaType);
    }
  }

  let durationMs: number | null = null;
  if (typeof durationInSeconds === "number") {
    // It's possible to create a valid MP4/M4A file with no audio/video tracks.
    // This will have a duration of 0. Also deals with weird values e.g. -Infinity / NaN
    if (durationInSeconds <= 0 || isNaN(durationInSeconds)) {
      throw new MediaDecodeFormatError(file.type, mediaType);
    }

    // Floor the duration to avoid floating point precision issues
    durationMs = Math.floor(durationInSeconds * 1000);
  }

  return {
    durationMs,
    mediaType,
    videoCodec,
    dimensions,
  };
}
